import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import { fetchUsers, deleteUser } from "./UserSlice";
import DataTable from "@components/ui/DataTable";
import type { Column } from "@components/ui/DataTable";
import { UserOutputDTO } from "./types";
import { LoadingSkeleton } from "@components/ui/LoadingSkeleton";
import { ErrorDisplay } from "@components/ui/ErrorDisplay";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import AgendaModal from "./AgendaModal";
import { Calendar } from "lucide-react";
import { API_URL } from "@/config/api";

const UserList = (props: any) => {
  console.log("🔄 Renderizando UserList");

  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => {
    console.log("🔍 Selector executando, loading:", state.user.loading);
    return state.user;
  });

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const [sector, setSector] = useState("");
  const [profile, setProfile] = useState("");
  const [meetings, setMeetings] = useState([]);

  const [openModal, setOpenModal] = useState(false);

  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );

  const loadData = useCallback(
    async (currentFilters: Record<string, string>) => {
      // Avoid simultaneous calls
      if (isLoadingRef.current) {
        console.log("🚫 Requisição em andamento, ignorando nova chamada");
        return;
      }

      // Avoid calls after initial load
      // if (initialLoadDoneRef.current && Object.keys(currentFilters).length === 0) {
      //   console.log('🚫 Ignorando chamada sem filtros após carregamento inicial');
      //   return;
      // }

      console.log("📡 Iniciando requisição fetchUsers");
      isLoadingRef.current = true;

      try {
        await dispatch(fetchUsers(currentFilters));
        await dispatch(fetchAffiliates({}));

        initialLoadDoneRef.current = true;
      } finally {
        isLoadingRef.current = false;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    console.log("🔄 useEffect - Carregando dados iniciais");
    loadData(filters);
    setIsInitialLoad(false);
  }, [loadData, filters]);

  useEffect(() => {
    if (props.setTotal) {
      props.setTotal(items?.length);
    }
  }, [items]);

  const handleFilterChange = (newFilters: Record<string, string>) => {
    console.log("🔍 Filtros alterados:", newFilters);
    setFilters(newFilters);
  };

  const handleDelete = async (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteUser(deleteId));
      setDeleteId(null);
      loadData(filters);
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  const columns: Column<UserOutputDTO>[] = [
    {
      image: "photoUrl",
      key: "name" as keyof UserOutputDTO,
      header: "Nome",
      render: (value: any) => String(value || ""),
    },
    // {
    //   key: 'email' as keyof UserOutputDTO,
    //   header: 'Email',
    //   render: (value: any) => String(value || ''),
    // },
    {
      key: "sector" as keyof UserOutputDTO,
      header: "Setor",
      // render: (value: any) => value ? UserStatusLabels[value as keyof typeof UserStatusLabels] || value : '-',
      render: (value: any) =>
        value
          ? affiliates[0]?.sectors.filter((item: any) => item._id == value)[0]
              ?.name || value
          : "-",
    },
    {
      key: "role" as keyof UserOutputDTO,
      header: "Perfil",
      render: (value: any) =>
        value
          ? affiliates[0]?.roles.filter((item: any) => item._id == value)[0]
              ?.name || value
          : "-",
    },
    // {
    //   key: 'createdAt' as keyof UserOutputDTO,
    //   header: 'Data de Criação',
    //   render: (value: any) => value ? new Date(value as Date).toLocaleDateString() : '-',
    // },
  ];

  const filterFields = [
    {
      name: "name",
      label: "Nome",
      type: "text",
    },
    {
      name: "email",
      label: "Email",
      type: "text",
    },
  ];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    console.log(name, value);
    if (name == "profile") {
      setProfile(value);
    } else if (name == "sector") {
      setSector(value);
    }
  };

  if (loading && isInitialLoad) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  console.log(props.hide_header);

  return (
    <div
      className={
        "flex flex-col flex-1 shrink  self-stretch  basis-0 min-w-60 max-md:px-5 bg-background-dark " +
        (props.hide_header != true ? " px-6 py-7" : "")
      }
    >
      {/* <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Colaboradores</h1>
        
      </div> */}

      {/* Temporarily commenting out DataFilter until we fix its interface */}
      {/* <DataFilter fields={filterFields} onFilterChange={handleFilterChange} /> */}
      <AgendaModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        meetings={meetings}
      />
      <DataTable
        hide_header={props.hide_header}
        columns={columns}
        children={() => (
          <div
            className="mb-4 flex w-full"
            style={{
              position: "relative",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <div style={{ width: "48%" }}>
              <label className="text-sm font-semibold text-gray-700">
                Setor ({affiliates[0]?.sectors?.length})
              </label>
              <select
                name="sector"
                value={sector}
                onChange={handleChange}
                required
                style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                className={`mt-1 h-12 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
              >
                <option value={""}>Selecione um setor</option>
                {/* <option value={'Geral'}>
                  Geral
                </option>
                <option value={'Comercial'}>
                  Comercial
                </option> */}

                {affiliates[0]?.sectors?.map((product: any) => (
                  <option key={product._id} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ width: "48%" }}>
              <label className="text-sm font-semibold text-gray-700 mb-1">
                Perfil ({affiliates[0]?.roles?.length})
              </label>
              <select
                name="profile"
                value={profile}
                onChange={handleChange}
                required
                style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                className={`h-12 mt-1 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
              >
                <option value={""}>Selecione um perfil</option>
                {affiliates[0]?.roles?.map((product: any) => (
                  <option key={product._id} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        actions={[
          {
            label: "Abrir agenda",
            icon: <Calendar />,
            onClick: async (item) => {
              let schedules_profissional = await (
                await fetch(
                  API_URL +
                    "/appointments/schedules/all/all/" +
                    item._id +
                    "?details=all",
                  {
                    headers: {
                      Authorization: localStorage.getItem("token"),
                      Accept: "application/json",
                    },
                  }
                )
              ).json();
              setMeetings(schedules_profissional);
              console.log(schedules_profissional);
              setOpenModal(true);
              // navigate(`/filled-forms/view/${item._id}`)
            },
          },
        ]}
        data={items
          .filter((item) => {
            if (sector == "") return true;
            else return item.sector == sector;
          })
          .filter((item) => {
            if (profile == "") return true;
            else return item.role == profile;
          })}
        title={"Colaboradores"}
        subtitle={"Lista de colaboradores cadatrados no sistema"}
        basePath="users"
        onDelete={handleDelete}
        loading={loading}
        error={error}
        newFormat
        hide_show

      />

      <Dialog open={!!deleteId} onClose={cancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este usuário? Esta ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserList;
